import {
    safetyPercentage,
    colorCode,
    riskProfile,
} from "../../utilities/helpers"
import { Link } from "react-router-dom"
import Modal from "react-responsive-modal"
import "./SidebarDashboard.css"
import { FLAGS_API_URL } from "../../const/paths"
import { useEffect, useState } from "react"
import { isAtRisk, isLocationStale } from "../../utilities/communities"

const STALE_OPACITY = 0.35

export default function SidebarDashboard({
    notifications = [],
    communities,
    isLoadingCommunities,
    addCommunityMember,
    removeCommunityMember,
    deleteCommunity,
    editCommunitySettings,
    editCommunityMember,
    reorderChaperees,
    togglePinMember,
    isSidebarOpen,
    setIsSidebarOpen,
    onShowNotifications,
    refreshCommunities,
}) {
    const [draggedItem, setDraggedItem] = useState(null)
    const [dragOverItem, setDragOverItem] = useState(null)
    const [hoveredCommunity, setHoveredCommunity] = useState(null)
    const [collapsedCommunities, setCollapsedCommunities] = useState({})
    const [deleteConfirmModal, setDeleteConfirmModal] = useState({
        isOpen: false,
        communityId: null,
        communityName: "",
        memberCount: 0,
    })
    const INITIAL_REMOVE_MEMBER_MODAL = {
        isOpen: false,
        communityId: null,
        communityName: "",
        memberId: null,
        memberType: "",
        memberName: "",
    }
    const [removeMemberConfirmModal, setRemoveMemberConfirmModal] = useState(
        INITIAL_REMOVE_MEMBER_MODAL,
    )
    const [isRemovingMember, setIsRemovingMember] = useState(false)

    // Helper function to check if current user is a community admin
    const isCommunityAdmin = (community) => {
        try {
            // Get current user from localStorage
            const storedUser = localStorage.getItem("chaperon_user_data")
            if (!storedUser) return false

            const currentUser = JSON.parse(storedUser)
            const currentUserId = currentUser?.id

            if (!currentUserId) return false

            // Parse chiefs array from community
            let chiefs = []
            if (community.chiefs) {
                chiefs =
                    typeof community.chiefs === "string"
                        ? JSON.parse(community.chiefs)
                        : community.chiefs
            }

            // Check if current user is a creator, admin, or chief
            return chiefs.some(
                (chief) =>
                    String(chief.id) === String(currentUserId) &&
                    (chief.role === "creator" ||
                        chief.role === "admin" ||
                        chief.role === "chief"),
            )
        } catch (error) {
            console.error("Error checking delete permission:", error)
            return false
        }
    }

    // Hide tooltips when sidebar content changes to prevent stale tooltips
    useEffect(() => {
        // Hide all visible tooltips before content updates
        const tooltipElements = document.querySelectorAll(
            '[data-bs-toggle="tooltip"]',
        )
        tooltipElements.forEach((el) => {
            try {
                const tooltip = window.bootstrap?.Tooltip?.getInstance(el)
                if (tooltip) {
                    tooltip.hide()
                }
            } catch (e) {
                // Silently handle errors for tooltips
            }
        })
        // MutationObserver in App.jsx will handle reinitializing tooltips for any new elements
    }, [communities, notifications, hoveredCommunity])

    const handleDragStart = (e, item, communityId) => {
        // Hide all visible tooltips before dragging
        const tooltipElements = document.querySelectorAll(
            '[data-bs-toggle="tooltip"]',
        )
        tooltipElements.forEach((el) => {
            try {
                const tooltip = window.bootstrap?.Tooltip?.getInstance(el)
                if (tooltip) {
                    tooltip.hide()
                }
            } catch (e) {
                // Silently handle errors for tooltips
            }
        })

        setDraggedItem({ item, communityId })
        e.dataTransfer.effectAllowed = "move"
    }

    const handleDragOver = (e, item) => {
        e.preventDefault()
        e.dataTransfer.dropEffect = "move"
        setDragOverItem(item.id)
    }

    const handleDragLeave = () => {
        setDragOverItem(null)
    }

    const handleDrop = (e, targetItem, communityId) => {
        e.preventDefault()
        setDragOverItem(null)

        if (!draggedItem || draggedItem.communityId !== communityId) {
            return
        }

        if (draggedItem.item.id === targetItem.id) {
            setDraggedItem(null)
            return
        }

        // Call the reorder handler
        if (reorderChaperees) {
            reorderChaperees(communityId, draggedItem.item.id, targetItem.id)
        }

        setDraggedItem(null)
    }
    const handleDeleteClick = (community) => {
        const memberCount =
            (community.persons?.length || 0) + (community.places?.length || 0)

        if (memberCount > 0) {
            // Show confirmation modal for communities with members
            setDeleteConfirmModal({
                isOpen: true,
                communityId: community.id,
                communityName: community.name,
                memberCount: memberCount,
            })
        } else {
            // Delete immediately if no members
            if (deleteCommunity) {
                deleteCommunity(community.id)
            }
        }
    }

    const toggleCommunityCollapse = (communityId) => {
        setCollapsedCommunities((prev) => ({
            ...prev,
            [communityId]: !prev[communityId],
        }))
    }

    const confirmDelete = () => {
        if (deleteCommunity && deleteConfirmModal.communityId) {
            deleteCommunity(deleteConfirmModal.communityId)
        }
        setDeleteConfirmModal({
            isOpen: false,
            communityId: null,
            communityName: "",
            memberCount: 0,
        })
    }

    const cancelDelete = () => {
        setDeleteConfirmModal({
            isOpen: false,
            communityId: null,
            communityName: "",
            memberCount: 0,
        })
    }

    const handleRemoveMemberClick = (community, item) => {
        const memberName =
            item?.full_name ||
            item?.username ||
            item?.name ||
            item?.fromGoogle?.name ||
            item?.email ||
            "this member"

        setRemoveMemberConfirmModal({
            isOpen: true,
            communityId: community.id,
            communityName: community.name,
            memberId: item.id,
            memberType: item.type,
            memberName,
        })
    }

    const confirmRemoveMember = async () => {
        if (
            removeCommunityMember &&
            removeMemberConfirmModal.communityId &&
            removeMemberConfirmModal.memberId &&
            removeMemberConfirmModal.memberType
        ) {
            setIsRemovingMember(true)
            try {
                await removeCommunityMember(
                    removeMemberConfirmModal.communityId,
                    removeMemberConfirmModal.memberId,
                    removeMemberConfirmModal.memberType,
                )
            } finally {
                setIsRemovingMember(false)
            }
        }

        setRemoveMemberConfirmModal(INITIAL_REMOVE_MEMBER_MODAL)
    }

    const cancelRemoveMember = () => {
        if (isRemovingMember) return
        setRemoveMemberConfirmModal(INITIAL_REMOVE_MEMBER_MODAL)
    }

    const handleDragEnd = () => {
        setDraggedItem(null)
        setDragOverItem(null)
    }

    return (
        <>
            {/* Independent Toggle Button - Always Visible */}
            <button
                id="dashboard-sidebar-drawer-handle"
                className={`btn btn-primary position-fixed shadow-sm ${
                    isSidebarOpen ? "sidebar-open" : "sidebar-closed"
                }`}
                onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            >
                {isSidebarOpen ? (
                    <i className="bi bi-chevron-right"></i>
                ) : (
                    <span>
                        MEMBERS <i className="bi bi-arrow-left-circle"></i>
                    </span>
                )}
            </button>

            {/* Floating Dashboard Sidebar */}
            <div
                id="dashboard-sidebar"
                className={`position-fixed ${
                    isSidebarOpen ? "sidebar-open" : "sidebar-closed"
                }`}
            >
                {/* Sidebar Content */}
                <div
                    id="dashboard-sidebar-content"
                    className="h-100 d-flex flex-column"
                >
                    {/* Header */}
                    <div
                        id="dashboard-sidebar-header"
                        className="sidebar-header p-4 border-bottom"
                    >
                        <div className="d-flex align-items-center justify-content-between">
                            <div
                                id="dashboard-sidebar-title"
                                className="text-start"
                            >
                                <h5 className="mb-1 fw-bold text-dark">
                                    Members
                                </h5>
                                <p className="mb-0 text-muted small">
                                    An overview of your safety interests
                                </p>
                            </div>
                            <div className="text-end">
                                <button
                                    id="notification-count-badge"
                                    type="button"
                                    className={`btn ${
                                        notifications.filter(
                                            (n) =>
                                                (n.status === "pending" ||
                                                    n.status === "unread") &&
                                                n.channel === "in_app",
                                        ).length > 0
                                            ? "btn-outline-danger"
                                            : "btn-outline-secondary"
                                    } fs-6 px-3 py-2`}
                                    onClick={onShowNotifications}
                                    aria-label="View notifications"
                                    data-bs-toggle="tooltip"
                                    data-bs-title="Notifications"
                                >
                                    {
                                        notifications.filter(
                                            (n) =>
                                                (n.status === "pending" ||
                                                    n.status === "unread") &&
                                                n.channel === "in_app",
                                        ).length
                                    }
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Scrollable Content */}
                    <div
                        id="dashboard-sidebar-scrollable-content"
                        className="flex-grow-1 overflow-auto"
                    >
                        {/* Community Section */}
                        <div id="community-section" className="p-4">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h6 className="fw-bold mb-0">COMMUNITIES</h6>
                                <div>
                                    <button
                                        id="refresh-communities"
                                        className="btn btn-sm btn-outline-secondary me-2"
                                        onClick={refreshCommunities}
                                        disabled={isLoadingCommunities}
                                        data-bs-toggle="tooltip"
                                        data-bs-title="Refresh data"
                                    >
                                        <i className="bi bi-arrow-clockwise"></i>
                                    </button>
                                    <button
                                        id="add-community-member"
                                        className="btn btn-sm btn-outline-secondary me-2"
                                        onClick={addCommunityMember}
                                        data-bs-toggle="tooltip"
                                        data-bs-title="Add new member"
                                    >
                                        <i className="bi bi-person-plus"></i>
                                    </button>
                                    <button
                                        className="btn btn-sm btn-outline-secondary"
                                        onClick={addCommunityMember}
                                        data-bs-toggle="tooltip"
                                        data-bs-title="Members"
                                    >
                                        <i className="bi bi-person-gear"></i>
                                    </button>
                                </div>
                            </div>

                            {/* Loading Placeholders */}
                            {isLoadingCommunities ? (
                                <div>
                                    <h6 className="fw-bold mt-4 bg-secondary-subtle p-2 rounded placeholder-glow">
                                        <span className="placeholder col-6"></span>
                                    </h6>
                                    <ul className="list-group list-group-flush">
                                        {[1, 2, 3].map((i) => (
                                            <li
                                                key={i}
                                                className="list-group-item d-flex justify-content-between align-items-center px-0"
                                            >
                                                <div
                                                    className="d-flex align-items-center placeholder-glow"
                                                    style={{ flex: "1" }}
                                                >
                                                    <span
                                                        className="placeholder placeholder-sm bg-secondary rounded-circle me-2"
                                                        style={{
                                                            width: "32px",
                                                            height: "32px",
                                                        }}
                                                    ></span>
                                                    <span className="placeholder col-7"></span>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ) : /* Empty State */
                            communities.length === 0 ||
                              communities.every(
                                  (c) =>
                                      (c.persons?.length || 0) === 0 &&
                                      (c.places?.length || 0) === 0,
                              ) ? (
                                <div className="text-center py-5 px-3">
                                    <div className="mb-4">
                                        <i
                                            className="bi bi-people text-muted"
                                            style={{ fontSize: "3rem" }}
                                        ></i>
                                    </div>
                                    <h6 className="fw-bold mb-3">
                                        Start Building Your Community
                                    </h6>
                                    <p className="text-muted small mb-4">
                                        Add members you care about to monitor
                                        safety in real-time. Stay connected and
                                        informed about the well-being of your
                                        loved ones and important places.
                                    </p>
                                    <button
                                        className="btn btn-primary btn-sm"
                                        onClick={addCommunityMember}
                                    >
                                        <i className="bi bi-person-plus me-2"></i>
                                        Add Your First Member
                                    </button>
                                </div>
                            ) : (
                                communities.map((community) => (
                                    <div key={community.id} className="mb-3">
                                        <h6
                                            className="fw-bold mt-4 bg-secondary-subtle p-2 rounded d-flex align-items-center justify-content-between"
                                            onMouseEnter={() =>
                                                setHoveredCommunity(
                                                    community.id,
                                                )
                                            }
                                            onMouseLeave={() =>
                                                setHoveredCommunity(null)
                                            }
                                        >
                                            <span>
                                                {community.name}
                                                <span
                                                    className="badge bg-secondary ms-3"
                                                    data-bs-toggle="tooltip"
                                                    data-bs-title="Number of members"
                                                >
                                                    {(community.persons
                                                        ?.length || 0) +
                                                        (community.places
                                                            ?.length || 0)}
                                                </span>
                                                {hoveredCommunity ===
                                                    community.id &&
                                                    isCommunityAdmin(
                                                        community,
                                                    ) && (
                                                        <i
                                                            className="bi bi-shield-check text-secondary ms-2"
                                                            data-bs-toggle="tooltip"
                                                            data-bs-title="You are a Community Chief"
                                                            style={{
                                                                cursor: "pointer",
                                                                fontSize:
                                                                    "0.9rem",
                                                            }}
                                                        ></i>
                                                    )}
                                            </span>
                                            <div className="d-flex align-items-center gap-2">
                                                {hoveredCommunity ===
                                                    community.id &&
                                                    isCommunityAdmin(
                                                        community,
                                                    ) && (
                                                        <>
                                                            <i
                                                                className="bi bi-gear text-secondary"
                                                                style={{
                                                                    cursor: "pointer",
                                                                    fontSize:
                                                                        "1rem",
                                                                }}
                                                                data-bs-toggle="tooltip"
                                                                data-bs-title="Community settings"
                                                                onClick={() =>
                                                                    editCommunitySettings(
                                                                        community,
                                                                    )
                                                                }
                                                            ></i>
                                                            <i
                                                                className="bi bi-trash text-secondary"
                                                                style={{
                                                                    cursor: "pointer",
                                                                    fontSize:
                                                                        "1rem",
                                                                }}
                                                                data-bs-toggle="tooltip"
                                                                data-bs-title="Delete community"
                                                                onClick={() =>
                                                                    handleDeleteClick(
                                                                        community,
                                                                    )
                                                                }
                                                            ></i>
                                                        </>
                                                    )}
                                                <i
                                                    className={
                                                        collapsedCommunities[
                                                            community.id
                                                        ]
                                                            ? "bi bi-chevron-down text-secondary"
                                                            : "bi bi-chevron-up text-secondary"
                                                    }
                                                    style={{
                                                        cursor: "pointer",
                                                        fontSize: "1rem",
                                                    }}
                                                    onClick={() =>
                                                        toggleCommunityCollapse(
                                                            community.id,
                                                        )
                                                    }
                                                    data-bs-toggle="tooltip"
                                                    data-bs-title={
                                                        collapsedCommunities[
                                                            community.id
                                                        ]
                                                            ? "Expand"
                                                            : "Collapse"
                                                    }
                                                ></i>
                                            </div>
                                        </h6>

                                        {/* Unified Chaperees Section */}
                                        {!collapsedCommunities[
                                            community.id
                                        ] && (
                                            <ul className="list-group list-group-flush">
                                                {[
                                                    ...(community.persons ||
                                                        []),
                                                    ...(community.places || []),
                                                ]
                                                    .sort((a, b) => {
                                                        if (
                                                            a.pinned &&
                                                            !b.pinned
                                                        )
                                                            return -1
                                                        if (
                                                            !a.pinned &&
                                                            b.pinned
                                                        )
                                                            return 1

                                                        const orderA =
                                                            a.order !==
                                                                undefined &&
                                                            a.order !== null
                                                                ? a.order
                                                                : Number.MAX_SAFE_INTEGER
                                                        const orderB =
                                                            b.order !==
                                                                undefined &&
                                                            b.order !== null
                                                                ? b.order
                                                                : Number.MAX_SAFE_INTEGER
                                                        if (orderA !== orderB)
                                                            return (
                                                                orderA - orderB
                                                            )
                                                        if (
                                                            a.updated_at &&
                                                            b.updated_at
                                                        ) {
                                                            return (
                                                                new Date(
                                                                    a.updated_at,
                                                                ) -
                                                                new Date(
                                                                    b.updated_at,
                                                                )
                                                            )
                                                        }
                                                        return a.id - b.id
                                                    })
                                                    .map((item) => (
                                                        <li
                                                            key={item.id}
                                                            draggable
                                                            onDragStart={(e) =>
                                                                handleDragStart(
                                                                    e,
                                                                    item,
                                                                    community.id,
                                                                )
                                                            }
                                                            onDragOver={(e) =>
                                                                handleDragOver(
                                                                    e,
                                                                    item,
                                                                )
                                                            }
                                                            onDragLeave={
                                                                handleDragLeave
                                                            }
                                                            onDrop={(e) =>
                                                                handleDrop(
                                                                    e,
                                                                    item,
                                                                    community.id,
                                                                )
                                                            }
                                                            onDragEnd={
                                                                handleDragEnd
                                                            }
                                                            className={`list-group-item d-flex justify-content-between align-items-center px-0 ${
                                                                dragOverItem ===
                                                                item.id
                                                                    ? "border-primary border-2"
                                                                    : ""
                                                            }`}
                                                            style={{
                                                                cursor: "move",
                                                                transition:
                                                                    "border 0.2s ease",
                                                            }}
                                                        >
                                                            <div
                                                                className="d-flex align-items-start"
                                                                style={{
                                                                    flex: "1",
                                                                    minWidth:
                                                                        "0",
                                                                }}
                                                            >
                                                                <i
                                                                    className="bi bi-grip-vertical text-muted me-2"
                                                                    style={{
                                                                        flexShrink:
                                                                            "0",
                                                                    }}
                                                                ></i>
                                                                {item.type ===
                                                                "person" ? (
                                                                    <div
                                                                        style={{
                                                                            flex: 1,
                                                                            minWidth: 0,
                                                                            display:
                                                                                "flex",
                                                                            flexDirection:
                                                                                "column",
                                                                        }}
                                                                    >
                                                                        <div
                                                                            style={{
                                                                                display:
                                                                                    "flex",
                                                                                alignItems:
                                                                                    "center",
                                                                                textAlign:
                                                                                    "left",
                                                                                wordWrap:
                                                                                    "break-word",
                                                                                overflowWrap:
                                                                                    "break-word",
                                                                            }}
                                                                        >
                                                                            {item.profile_picture_url ? (
                                                                                <img
                                                                                    src={
                                                                                        item.profile_picture_url
                                                                                    }
                                                                                    alt={
                                                                                        item.username
                                                                                    }
                                                                                    className={`flag-avatar flag-avatar-sm border-${colorCode(safetyPercentage(item.overall_safety), "class")} me-2`}
                                                                                    style={{
                                                                                        flexShrink: 0,
                                                                                    }}
                                                                                />
                                                                            ) : (
                                                                                <i
                                                                                    className={`border-${colorCode(safetyPercentage(item.overall_safety), "class")} bi bi-person-circle me-2 text-muted text-${colorCode(safetyPercentage(item.overall_safety), "class")}`}
                                                                                    style={{
                                                                                        fontSize:
                                                                                            "1.5rem",
                                                                                        flexShrink: 0,
                                                                                    }}
                                                                                ></i>
                                                                            )}
                                                                            <span
                                                                                style={{
                                                                                    wordBreak:
                                                                                        "break-word",
                                                                                    opacity:
                                                                                        isLocationStale(
                                                                                            item,
                                                                                        )
                                                                                            ? STALE_OPACITY
                                                                                            : 1,
                                                                                }}
                                                                            >
                                                                                {item?.full_name ||
                                                                                    item?.username ||
                                                                                    item?.email}
                                                                            </span>
                                                                        </div>
                                                                        {item.entity_name && (
                                                                            <div
                                                                                className="small text-muted mt-1 ms-5"
                                                                                style={{
                                                                                    textAlign:
                                                                                        "left",
                                                                                    opacity:
                                                                                        isLocationStale(
                                                                                            item,
                                                                                        )
                                                                                            ? STALE_OPACITY
                                                                                            : 1,
                                                                                }}
                                                                            >
                                                                                <Link
                                                                                    to={`/analysis/${item.slug}`}
                                                                                    className={`text-decoration-none text-${colorCode(safetyPercentage(item.overall_safety))}`}
                                                                                >
                                                                                    <img
                                                                                        src={`${FLAGS_API_URL}/${item.country_code}.svg`}
                                                                                        alt={
                                                                                            item.entity_name
                                                                                        }
                                                                                        style={{
                                                                                            width: "20px",
                                                                                            height: "auto",
                                                                                            marginRight:
                                                                                                "4px",
                                                                                        }}
                                                                                    />
                                                                                    {
                                                                                        item.entity_name
                                                                                    }
                                                                                </Link>
                                                                            </div>
                                                                        )}
                                                                        {!item.entity_name && (
                                                                            <div
                                                                                className="small text-muted mt-1"
                                                                                style={{
                                                                                    textAlign:
                                                                                        "left",
                                                                                    opacity:
                                                                                        isLocationStale(
                                                                                            item,
                                                                                        )
                                                                                            ? STALE_OPACITY
                                                                                            : 1,
                                                                                }}
                                                                            >
                                                                                <kbd className="small text-muted bg-light ms-5">
                                                                                    {item.status ===
                                                                                    "invited"
                                                                                        ? "invited"
                                                                                        : item.latitude ==
                                                                                                null ||
                                                                                            item.longitude ==
                                                                                                null
                                                                                          ? "No location data"
                                                                                          : "No data available yet"}
                                                                                </kbd>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                ) : (
                                                                    <div
                                                                        style={{
                                                                            flex: 1,
                                                                            minWidth: 0,
                                                                        }}
                                                                    >
                                                                        <Link
                                                                            to={`/analysis/${item.slug}`}
                                                                            className="text-decoration-none text-secondary"
                                                                            style={{
                                                                                wordWrap:
                                                                                    "break-word",
                                                                                overflowWrap:
                                                                                    "break-word",
                                                                                minWidth:
                                                                                    "0",
                                                                                display:
                                                                                    "flex",
                                                                                alignItems:
                                                                                    "flex-start",
                                                                            }}
                                                                        >
                                                                            <img
                                                                                src={`${FLAGS_API_URL}/${item?.country_code}.svg`}
                                                                                data-bs-toggle="tooltip"
                                                                                data-bs-title={
                                                                                    item
                                                                                        ?.fromGoogle
                                                                                        ?.name ||
                                                                                    item?.name
                                                                                }
                                                                                alt={
                                                                                    item
                                                                                        ?.fromGoogle
                                                                                        ?.name ||
                                                                                    item?.name
                                                                                }
                                                                                className={`flag-avatar flag-avatar-md border-${colorCode(safetyPercentage(item?.overall_safety), "class")}`}
                                                                                style={{
                                                                                    marginRight:
                                                                                        "8px",
                                                                                    cursor: "pointer",
                                                                                    flexShrink:
                                                                                        "0",
                                                                                }}
                                                                            />
                                                                            <span
                                                                                style={{
                                                                                    wordBreak:
                                                                                        "break-word",
                                                                                }}
                                                                            >
                                                                                {item
                                                                                    ?.fromGoogle
                                                                                    ?.name ||
                                                                                    item?.name}
                                                                            </span>
                                                                        </Link>
                                                                    </div>
                                                                )}
                                                            </div>

                                                            <div>
                                                                {/* != null (not truthiness): a raw score of 0 is the most
                                                                    dangerous reading and must still trigger the alert icon */}
                                                                {isAtRisk(item) && (
                                                                        <span
                                                                            data-bs-toggle="tooltip"
                                                                            data-bs-title={riskProfile(
                                                                                safetyPercentage(
                                                                                    item.overall_safety,
                                                                                ),
                                                                            )}
                                                                            className={`text-${colorCode(
                                                                                safetyPercentage(
                                                                                    item.overall_safety,
                                                                                ),
                                                                            )} fs-7 safety-sidebar-badge`}
                                                                        >
                                                                            {riskProfile(
                                                                                safetyPercentage(
                                                                                    item.overall_safety,
                                                                                ),
                                                                                "icons",
                                                                            )}
                                                                        </span>
                                                                    )}

                                                                {isCommunityAdmin(
                                                                    community,
                                                                ) && (
                                                                    <span id="person-action-icons">
                                                                        <i
                                                                            className={`bi ${item.pinned ? "bi-pin-angle-fill text-primary" : "bi-pin-angle text-secondary"} me-2`}
                                                                            data-bs-toggle="tooltip"
                                                                            data-bs-title={`${item.pinned ? "Unpin" : "Pin"} ${item.type}`}
                                                                            style={{
                                                                                cursor: "pointer",
                                                                            }}
                                                                            onClick={() =>
                                                                                togglePinMember(
                                                                                    item.id,
                                                                                    community.id,
                                                                                    item.type,
                                                                                )
                                                                            }
                                                                        ></i>
                                                                        <i
                                                                            className="bi bi-pencil text-secondary me-2"
                                                                            data-bs-toggle="tooltip"
                                                                            data-bs-title={`Edit ${item.type}`}
                                                                            style={{
                                                                                cursor: "pointer",
                                                                            }}
                                                                            onClick={() =>
                                                                                editCommunityMember(
                                                                                    item,
                                                                                    community.id,
                                                                                )
                                                                            }
                                                                        ></i>
                                                                        <i
                                                                            className="bi bi-x-circle text-secondary"
                                                                            data-bs-toggle="tooltip"
                                                                            data-bs-title={`Remove ${item.type}`}
                                                                            style={{
                                                                                cursor: "pointer",
                                                                            }}
                                                                            onClick={() =>
                                                                                handleRemoveMemberClick(
                                                                                    community,
                                                                                    item,
                                                                                )
                                                                            }
                                                                        ></i>
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </li>
                                                    ))}
                                            </ul>
                                        )}
                                    </div>
                                ))
                            )}
                        </div>
                    </div>

                    {/* Footer */}
                    <div
                        id="dashboard-sidebar-footer"
                        className="sidebar-footer p-3 border-top"
                    >
                        <div className="d-flex justify-content-between align-items-center">
                            <small className="text-muted">
                                <i className="bi bi-clock me-1"></i>
                                Last updated: {new Date().toLocaleTimeString()}
                            </small>
                        </div>
                    </div>
                </div>
            </div>

            {/* Delete Confirmation Modal */}
            <Modal
                open={deleteConfirmModal.isOpen}
                onClose={cancelDelete}
                center
                classNames={{
                    modal: "customModal",
                }}
            >
                <div className="modal-content">
                    <div className="modal-header mb-3">
                        <h4>
                            <i className="bi bi-exclamation-triangle text-warning pe-2"></i>
                            Confirm Deletion
                        </h4>
                    </div>
                    <div className="modal-body p-3">
                        <p>
                            Are you sure you want to delete{" "}
                            <strong>
                                "{deleteConfirmModal.communityName}"
                            </strong>
                            ?
                        </p>
                        <p className="text-muted">
                            This community has{" "}
                            <strong>{deleteConfirmModal.memberCount}</strong>{" "}
                            member
                            {deleteConfirmModal.memberCount > 1 ? "s" : ""} and
                            this action cannot be undone.
                        </p>
                    </div>
                    <div className="modal-footer d-flex gap-2 justify-content-end p-3">
                        <button
                            className="btn btn-danger"
                            onClick={confirmDelete}
                        >
                            <i className="bi bi-trash me-2"></i>
                            Delete Community
                        </button>
                        <button
                            className="btn btn-secondary"
                            onClick={cancelDelete}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </Modal>

            {/* Remove Member Confirmation Modal */}
            <Modal
                open={removeMemberConfirmModal.isOpen}
                onClose={cancelRemoveMember}
                center
                classNames={{
                    modal: "customModal",
                }}
            >
                <div className="modal-content">
                    <div className="modal-header mb-3">
                        <h4>
                            <i className="bi bi-exclamation-triangle text-warning pe-2"></i>
                            Confirm Member Removal
                        </h4>
                    </div>
                    <div className="modal-body p-3">
                        <p>
                            Are you sure you want to remove{" "}
                            <strong>
                                "{removeMemberConfirmModal.memberName}"
                            </strong>{" "}
                            from{" "}
                            <strong>
                                "{removeMemberConfirmModal.communityName}"
                            </strong>
                            ?
                        </p>
                        <p className="text-muted">
                            This member will no longer be monitored in this
                            community.
                        </p>
                    </div>
                    <div className="modal-footer d-flex gap-2 justify-content-end p-3">
                        <button
                            className="btn btn-danger"
                            onClick={confirmRemoveMember}
                            disabled={isRemovingMember}
                        >
                            {isRemovingMember ? (
                                <>
                                    <span
                                        className="spinner-border spinner-border-sm me-2"
                                        role="status"
                                        aria-hidden="true"
                                    ></span>
                                    Removing...
                                </>
                            ) : (
                                <>
                                    <i className="bi bi-person-dash me-2"></i>
                                    Remove Member
                                </>
                            )}
                        </button>
                        <button
                            className="btn btn-secondary"
                            onClick={cancelRemoveMember}
                            disabled={isRemovingMember}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </Modal>
        </>
    )
}
